import type { ShallowRef } from "vue"

import { uuidv7 } from "uuidv7"

import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { PropertyId, PropertySchema, PropertyStatus } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { sendConversationPropertiesRequests } from "~/classes/handlers/conversation/properties"
import { sendConversationUpdateRequests } from "~/classes/handlers/conversation/update"

import { withReactive } from "../entity/ReactiveEntity"
import { BaseConversation } from "./BaseConversation"

export interface ReactiveConversationOptions {
  /**
   * If true, the conversation properties will be requested immediately after the component is mounted
   * @default false
   */
  immediate?: boolean
  /**
   * If true, the conversation properties will not be requested from the server, but updates will
   * still be integrated via events
   * @default false
   */
  noRequest?: boolean
  /**
   * If true, the file will not be registered for lifecycle events (mount/unmount)
   * @default false
   */
  noLifecycle?: boolean
}

export class ReactiveConversation extends withReactive<
  ConversationProperty,
  Conversation,
  StaticConversation,
  typeof BaseConversation
>(BaseConversation) {
  _inner: ShallowRef<StaticConversation> | WritableComputedRef<StaticConversation>
  location: SelectionLocation
  _innerId: string

  isSetup = false
  isDisposed = false
  options?: ReactiveConversationOptions
  fetchSelection: InterfaceSelection

  constructor({ conversation, selection, location, options }: {
    conversation: Ref<StaticConversation>
    selection?: InterfaceSelection
    location: SelectionLocation
    options?: ReactiveConversationOptions
  }) {
    super()
    this._inner = conversation
    this._innerId = conversation.value.id
    this.location = location
    this.fetchSelection = selection ?? InterfaceSelection.minimumConversationViewSelection()
    this.options = options

    // in immediate mode we have to register the callbacks immediately
    if (this.options?.immediate)
      this.use()

    if (!this.options?.noLifecycle) {
      onMounted(this.use)
      onUnmounted(this.dispose)
    }
  }

  intoStatic = () => new StaticConversation(this.inner())
  inner = () => this._inner.value.inner()

  get _conversation() {
    return this._inner.value
  }

  set _conversation(conversation: StaticConversation) {
    this._inner.value = conversation
  }

  refresh = () => {
    const { isOffline } = useCache()

    if (this.options?.noRequest)
      return
    if (!this._inner.value)
      throw new Error("Cannot request conversation properties when conversation is undefined")

    sendConversationPropertiesRequests([{
      requestId: generateWorkerRequestId(),
      conversationId: this._inner.value.id,
      offline: isOffline(),
      selection: this.fetchSelection.inner(),
    }])
  }

  onConversationPropertiesChanged = (conversation: ConversationEvent) => {
    if (conversation.id !== this._inner.value?.id)
      return console.warn("Reactive conversation received properties for a different conversation", conversation, this._inner.value)
    if (!this._inner.value)
      this._inner.value = new StaticConversation(conversation)
    this._inner.value.mergeWithNewer(new StaticConversation(conversation))
  }

  mergeWithNewer<T = StaticConversation>(later?: T): boolean {
    const newer = later as StaticConversation | undefined
    if (!newer)
      return false

    const changed = this._inner.value.mergeWithNewer(newer)
    if (changed)
      this._inner.value = new StaticConversation(this.inner())

    return changed
  }

  addProperty(property: ConversationProperty) {
    const conversation = StaticConversation.blank(this.inner().id)
    conversation.addProperty(property)
    this.mergeWithNewer(conversation)
  }

  override removeProperty(property: PropertyId): void {
    this.asStatic().removeProperty(property)
  }

  updateProperty = <Req = ConversationUpdateRequestDatagram>(
    propertyId: PropertyId,
    newValue: EntityPropertyValue,
    deleted?: boolean,
    unversioned?: boolean,
  ): {
    updated: StaticConversation
    request: Req
  } => {
    const { state: myself } = useSelf()
    const { isOffline } = useCache()

    if (!myself.value.user)
      throw new Error("Anon users can't update files")

    let version = 0
    const schema = newValue.type
    const timestamp = uuidv7()

    // If we're offline we don't increment the version (everything is
    // pending). If the property is unversioned, it doesn't get an
    // incremented property
    const prop = this.inner().properties?.[propertyId]

    if (prop && !isOffline() && !unversioned) {
      version = prop.version + 1
    }
    else if (prop && isOffline()) {
      version = prop.version
    }

    const newConvo = StaticConversation.blank(this.id)
    newConvo.addProperty({
      id: newConvo.id,
      propertyId,
      version,
      status: PropertyStatus.StatusPending,
      timestamp,
      value: newValue,
      deleted,
      schema,
      author: myself.value.user.id,
    })

    const request: ConversationUpdateRequestDatagram = {
      requestId: generateWorkerRequestId(),
      conversation: newConvo.inner(),
      offline: false,
    }
    sendConversationUpdateRequests([request])

    return {
      updated: new StaticConversation(request.conversation),
      request: request as Req,
    }
  }

  updateConversationTitle(title: string) {
    const { isOffline } = useCache()
    const { state: myself } = useSelf()
    if (!myself.value.user)
      throw new Error("Cannot update conversation title without a user")
    const userId = myself.value.user.id

    if (!isDefined(this._inner.value))
      return console.error("Cannot update title of undefined conversation")

    const titleProperty = this._inner.value.property(PropertyId.CONVERSATION_USER_TITLE)
    let version = 0
    if (titleProperty && !isOffline() && titleProperty.status === PropertyStatus.StatusCurrent)
      version = titleProperty.version + 1
    else if (titleProperty)
      version = titleProperty.version

    title = title.trim()
    if (!isDefined(title) || title.length === 0)
      return console.error("Cannot change to empty title")
    const conversation = StaticConversation.blank(this._inner.value.id)
    conversation.addProperty({
      id: this._inner.value.id,
      propertyId: PropertyId.CONVERSATION_USER_TITLE,
      value: {
        type: PropertySchema.NonEmptyStringValue,
        nonEmptyString: title,
      },
      version,
      schema: PropertySchema.NonEmptyStringValue,
      author: userId,
      timestamp: uuidv7(),
      status: PropertyStatus.StatusPending,
    })
    sendConversationUpdateRequests([{
      requestId: generateWorkerRequestId(),
      conversation: conversation.inner(),
      offline: isOffline(),
    }])

    // finally, update our current conversation object.
    this.mergeWithNewer(conversation)
  }

  use = () => {
    if (this.isSetup)
      return this

    this.isSetup = true

    const { addEntityEventListener } = useEvents()

    if (this._inner.value) {
      const conversationId = this._inner.value.id
      addEntityEventListener("conversationProperties", conversationId, this.onConversationPropertiesChanged)
    }

    if (this.options?.immediate)
      this.refresh()

    return this
  }

  dispose = () => {
    const { removeEntityEventListener } = useEvents()

    if (this.isDisposed)
      return console.error("Reactive conversation was already disposed")
    this.isDisposed = true

    removeEntityEventListener("conversationProperties", this._innerId, this.onConversationPropertiesChanged)
  }
}
